import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ConfigProvider } from 'antd';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useParams } from "react-router-dom";
import AuthProvider, { useAuth } from './assets/utils/useAuth'
import ProtectedRoute from './components/ProtectedRoute';
import { store } from './app/store';
import reportWebVitals from './reportWebVitals';
import './index.css';
import './styles/admin-ui.css';
import './features/index/App.css';
import Layout from './features/layout';
import ClientVerufyHook from './features/clients';
import CheckinListHook from './features/checkins';
import LoginPageHook from './features/login';
import AuthorizationRequestHook from './features/preauth';
import ClaimsHook from './features/claims';
import SuperAdmin from './features/dash';
import RBACAdmin from './features/rbacAdmin';
import Dashboard from './features/dashboard';
import PADashboard from './features/preauth/PADashboard';
import EnrolleeDashboard from './features/enrolleeDashboard';
import PAFiling from './features/preauth/paFiling';
import ClaimFiling from './features/claims/claimsFiling'
import ClientDetails from './features/search';
import Helpers from './assets/utils/Helpers';

import { useDispatch } from 'react-redux'
import Organizations from './features/orgs';
import OrganizationDetail from './features/orgs/organizationalDetail';
import PlanRequestsQueue from './features/orgs/PlanRequestsQueue';
import StaffUploadsQueue from './features/orgs/StaffUploadsQueue';
import EnrollmentConflictsQueue from './features/orgs/EnrollmentConflictsQueue';
import EnrollmentConflictsLegacyRedirect from './features/orgs/EnrollmentConflictsLegacyRedirect';
import FacilitiesList from './features/facilities';
import FacilityDetail from './features/facilities/FacilityDetail';
import FacilitySuggestions from './features/facilities/FacilitySuggestions';
import Plans from './features/plans';
import PlanDetail from './features/plans/planDetail';
import { Logs } from './features/logging';
import EncounterHook from './features/preauthFiling';
import FacilitySummaryPage from './features/checkins/FacilitySummaryPage';
import FacilityClaims from './features/claims/facilityClaims';
import PendingSubmissionsPage from './features/claims/PendingSubmissionsPage';
import SoftDeletedCheckinsList from './components/Admin/SoftDeletedCheckinsList';
import AdminAppointmentsList from './components/Admin/AdminAppointmentsList';
import AdminNotificationCenter from './components/Notifications/AdminNotificationCenter';
import { NotificationsRoute, signOutNotify } from './features/notify';
import NotificationSystemPage from './features/notify/NotificationSystemPage';
import LiveStatsWidget from './components/Dashboard/LiveStatsWidget';
import AdminNotificationPreferences from './components/Settings/AdminNotificationPreferences';
import CampaignsPage from './components/Notifications/CampaignsPage';
import AdminTariffDetail from './components/Tariffs/AdminTariffDetail';
import FacilityTariffList from './components/Tariffs/FacilityTariffList';
import StandardTariffView from './components/Tariffs/StandardTariffView';
import StandardTariffDetail from './components/Tariffs/StandardTariffDetail';
import AdminCheckinTypeDetail from './components/CheckinTypes/AdminCheckinTypeDetail';
import AdminCareProgramDetail from './components/CarePrograms/AdminCareProgramDetail';
import AdminCareProgramUtilization from './components/CarePrograms/AdminCareProgramUtilization';
import SaaSProRedirect from './features/integrations/saaspro/SaaSProRedirect';
import PartnersAdminPage from './features/integrations/partners';
import PlatformContextsPage from './features/platformContexts';
import IdentityGenerationSettingsPage from './features/identityGenerationSettings';
import OpsEmailRecipientsPage from './features/opsEmailRecipients';
import SmsProviderSettingsPage from './features/smsProviderSettings';
import ClaimsRolloutPage from './features/claimsRollout';
import TestSubjectsPage from './features/testSubjects';
import FacilityRatingsPage from './features/facilityRatings';
import HelpContentPage from './features/helpContent';
import FacilityTypesPage from './features/facilityTypes';
import PlanManagementPage from './features/planManagement';
import OnboardingOtpLookupPage from './features/onboardingOtp';
import AnnouncementsPage from './features/announcements';
import FacilityNoticesPage from './features/facilityNotices';
import FormSettingsPage from './features/formSettings';

/** CRA overlay treats extension script errors as app crashes — ignore them in dev. */
if (process.env.NODE_ENV === 'development') {
  const isExtensionSource = (value) =>
    typeof value === 'string' && value.includes('chrome-extension://');

  window.addEventListener(
    'error',
    (event) => {
      if (isExtensionSource(event.filename)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const reason = event.reason;
      const stack = reason?.stack || reason?.message || String(reason ?? '');
      if (isExtensionSource(stack)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );
}

const RedirectLegacyCheckinType = () => {
  const { checkinTypeId } = useParams();
  return <Navigate to={`/plans/encounter-types/${checkinTypeId}`} replace />;
};

const RedirectLegacyCareProgram = () => {
  const { programId } = useParams();
  return <Navigate to={`/plans/care-programs/${programId}`} replace />;
};

const container = document.getElementById('root');
const root = createRoot(container);

String.prototype.__capitalize = function () {
  const words = this.split(' ');
  const capitalizedString = words
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

  return capitalizedString;
};

// Convert the entire string to uppercase
String.prototype.__upper = function () {
  return this.toUpperCase();
};

// Convert the entire string to lowercase
String.prototype.__lower = function () {
  return this.toLowerCase();
};

const Logout = () => {
  const { set } = useAuth();
  const navigate = useNavigate();
  const dispatcher = useDispatch();

  useEffect(() => {
    // Notification System v2: this browser stops receiving this user's pushes (§4.7).
    signOutNotify();
    Helpers.logout(set, dispatcher);
    navigate('/login');
  }, []) //eslint-disable-line react-hooks/exhaustive-deps

  return (
    <></>
  )
}

root.render(
  <React.StrictMode>
    <ConfigProvider
      theme={{
        token: {
          fontFamily:
            "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
      }}
    >
    <AuthProvider>
      <Provider store={store}>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPageHook />} />
            <Route path="/logout" element={<Logout />} />

            <Route element={<Layout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path='/verify' element={<ProtectedRoute module="clientVerification"><ClientVerufyHook /></ProtectedRoute>} />
              <Route path='/search' element={<ProtectedRoute module="search"><ClientDetails /></ProtectedRoute>} />
              <Route path='/checkins' element={<ProtectedRoute module="encounters"><CheckinListHook /></ProtectedRoute>} />
              {/* Facility summary lives under Encounters; it keeps the claims module it always needed. */}
              <Route path='/checkins/facility-summary' element={<ProtectedRoute module="encounters"><FacilitySummaryPage /></ProtectedRoute>} />
              <Route path='/admin/soft-deleted-checkins' element={<ProtectedRoute module="checkinCleanup"><SoftDeletedCheckinsList /></ProtectedRoute>} />
              <Route path='/admin/appointments' element={<ProtectedRoute module="appointments"><AdminAppointmentsList /></ProtectedRoute>} />
              <Route path='/notifications' element={<ProtectedRoute module="notificationsCenter"><NotificationsRoute legacy={<AdminNotificationCenter />} /></ProtectedRoute>} />
              <Route path='/admin/settings/notification-system' element={<ProtectedRoute module="notificationSystem"><NotificationSystemPage /></ProtectedRoute>} />
              <Route path='/admin/campaigns' element={<ProtectedRoute module="broadcastNotifications"><CampaignsPage /></ProtectedRoute>} />
              <Route path='/admin/notifications/broadcast' element={<Navigate to="/admin/campaigns?channel=push" replace />} />
              <Route path='/admin/email-campaigns' element={<Navigate to="/admin/campaigns?channel=email" replace />} />
              <Route path='/announcements' element={<Navigate to="/admin/announcements" replace />} />
              <Route path='/admin/notification-preferences' element={<ProtectedRoute module="notificationsCenter"><AdminNotificationPreferences /></ProtectedRoute>} />
              <Route path='/admin/settings/notifications' element={<ProtectedRoute module="opsEmailRecipients"><OpsEmailRecipientsPage /></ProtectedRoute>} />
              <Route path='/admin/settings/sms-provider' element={<ProtectedRoute module="smsProviderSettings"><SmsProviderSettingsPage /></ProtectedRoute>} />
              <Route path='/admin/settings/claims-rollout' element={<ProtectedRoute module="claimsRollout"><ClaimsRolloutPage /></ProtectedRoute>} />
              <Route path='/admin/settings/test-subjects' element={<ProtectedRoute module="testSubjects"><TestSubjectsPage /></ProtectedRoute>} />
              <Route path='/admin/facility-ratings' element={<ProtectedRoute module="facilityRatings"><FacilityRatingsPage /></ProtectedRoute>} />
              <Route path='/admin/help-content' element={<ProtectedRoute module="helpContent"><HelpContentPage /></ProtectedRoute>} />
              <Route path='/admin/settings/facility-types' element={<ProtectedRoute module="facilityTypes"><FacilityTypesPage /></ProtectedRoute>} />
              <Route path='/admin/plan-management' element={<ProtectedRoute module="planManagement"><PlanManagementPage /></ProtectedRoute>} />
              <Route path='/settings/enrollment-segments' element={<Navigate to="/plans?tab=enrollment-segments" replace />} />
              <Route path='/admin/enrollment-segments' element={<Navigate to="/plans?tab=enrollment-segments" replace />} />
              <Route path='/preauth' element={<ProtectedRoute module="preauth"><AuthorizationRequestHook /></ProtectedRoute>} />
              <Route path='/preauth-action' element={<ProtectedRoute module="preauth"><PAFiling /></ProtectedRoute>} />
              <Route path='/file-encounter' element={<ProtectedRoute module="preauth"><EncounterHook /></ProtectedRoute>} />
              <Route path='/dash' element={<ProtectedRoute module="staffManagement"><SuperAdmin /></ProtectedRoute>} />
              <Route path='/rbac' element={<ProtectedRoute module="rbacAdmin"><RBACAdmin /></ProtectedRoute>} />
              <Route path='/platform/contexts' element={<ProtectedRoute permission="platform.*"><PlatformContextsPage /></ProtectedRoute>} />
              <Route path='/platform/identity-generation-settings' element={<ProtectedRoute permission="platform.*"><IdentityGenerationSettingsPage /></ProtectedRoute>} />
              <Route path='/admin/onboarding-otp' element={<ProtectedRoute module="onboardingOtp"><OnboardingOtpLookupPage /></ProtectedRoute>} />
              <Route path='/admin/announcements' element={<ProtectedRoute module="announcements"><AnnouncementsPage /></ProtectedRoute>} />
              <Route path='/admin/facility-notices' element={<ProtectedRoute module="facilityNotices"><FacilityNoticesPage /></ProtectedRoute>} />
              <Route path='/admin/form-settings' element={<ProtectedRoute module="formSettings"><FormSettingsPage /></ProtectedRoute>} />
              <Route path='/dashboard' element={<ProtectedRoute module="dashboard"><Dashboard /></ProtectedRoute>} />
              <Route path='/dashboard/preauth' element={<ProtectedRoute module="preauthAnalytics"><PADashboard /></ProtectedRoute>} />
              <Route path='/dashboard/enrollees' element={<ProtectedRoute module="dashboard"><EnrolleeDashboard /></ProtectedRoute>} />

              <Route path='/claims-action' element={<ProtectedRoute module="claims"><ClaimFiling /></ProtectedRoute>} />
              <Route path='/claims'>
                <Route index element={<ProtectedRoute module="claims"><ClaimsHook /></ProtectedRoute>} />
                <Route path='facility-summary' element={<Navigate to="/checkins/facility-summary" replace />} />
                <Route path='pending-submissions' element={<ProtectedRoute module="claims"><PendingSubmissionsPage /></ProtectedRoute>} />
                <Route path='/claims/filings'>
                  <Route index element={<ProtectedRoute module="claims"><ClaimFiling /></ProtectedRoute>} />
                  <Route path='/claims/filings/:facility/:segment' element={<ProtectedRoute module="claims"><FacilityClaims /></ProtectedRoute>} />
                </Route>
              </Route>

              <Route path='/corporations' >
                <Route index element={<ProtectedRoute module="organizations"><Organizations /></ProtectedRoute>} />
                <Route path='plan-requests' element={<ProtectedRoute module="planRequests"><PlanRequestsQueue /></ProtectedRoute>} />
                <Route path='staff-uploads' element={<ProtectedRoute module="organizations"><StaffUploadsQueue /></ProtectedRoute>} />
                <Route path='enrollment-holds' element={<ProtectedRoute module="organizations"><EnrollmentConflictsQueue /></ProtectedRoute>} />
                <Route path='enrollment-conflicts' element={<EnrollmentConflictsLegacyRedirect />} />
                <Route path='/corporations/users' element={<Navigate to="/corporations" />} />
                <Route path='/corporations/users/:org' element={<ProtectedRoute module="organizations"><OrganizationDetail /></ProtectedRoute>} />
              </Route>
              <Route path='/facilities'>
                <Route index element={<ProtectedRoute module="facilities"><FacilitiesList /></ProtectedRoute>} />
                <Route path="suggestions" element={<ProtectedRoute module="facilitySuggestions"><FacilitySuggestions /></ProtectedRoute>} />
                <Route path='/facilities/:facilityId' element={<ProtectedRoute module="facilities"><FacilityDetail /></ProtectedRoute>} />
                {/* Facility-scoped tariff list and detail (must be before catch-all) */}
                <Route path='/facilities/:facilityId/tariffs/:tariffId' element={<ProtectedRoute module="tariffs"><AdminTariffDetail /></ProtectedRoute>} />
                <Route path='/facilities/:facilityId/tariffs' element={<ProtectedRoute module="tariffs"><FacilityTariffList /></ProtectedRoute>} />
              </Route>

              {/* Standard tariff: list and detail (detail route must be before :tariffId) */}
              <Route path='/admin/integrations/partners/:partnerId' element={<ProtectedRoute module="integrationsSaaSPro"><PartnersAdminPage /></ProtectedRoute>} />
              <Route path='/admin/integrations/partners' element={<ProtectedRoute module="integrationsSaaSPro"><PartnersAdminPage /></ProtectedRoute>} />
              <Route path='/admin/integrations/saaspro' element={<ProtectedRoute module="integrationsSaaSPro"><SaaSProRedirect /></ProtectedRoute>} />
              <Route path='/admin/tariffs' element={<ProtectedRoute module="tariffs"><StandardTariffView /></ProtectedRoute>} />
              <Route path='/admin/tariffs/standard/:tariffId' element={<ProtectedRoute module="tariffs"><StandardTariffDetail /></ProtectedRoute>} />
              <Route path='/admin/tariffs/:tariffId' element={<ProtectedRoute module="tariffs"><AdminTariffDetail /></ProtectedRoute>} />

              <Route path='/log' element={<ProtectedRoute module="activityLog"><Logs /></ProtectedRoute>} />

              <Route path='/plans' >
                <Route index element={<ProtectedRoute module="plans"><Plans /></ProtectedRoute>} />
                <Route path='encounter-types/:checkinTypeId' element={<ProtectedRoute module="plans"><AdminCheckinTypeDetail /></ProtectedRoute>} />
                <Route path='care-programs/utilization' element={<ProtectedRoute module="careProgramUtilization"><AdminCareProgramUtilization /></ProtectedRoute>} />
                <Route path='care-programs/:programId' element={<ProtectedRoute module="careProgramsDesigner"><AdminCareProgramDetail /></ProtectedRoute>} />
                <Route path=':plan' element={<ProtectedRoute module="plans"><PlanDetail /></ProtectedRoute>} />
              </Route>
              <Route path='/admin/care-programs' element={<Navigate to="/plans?tab=care-programs" replace />} />
              <Route path='/admin/care-programs/:programId' element={<RedirectLegacyCareProgram />} />
              <Route path='/admin/checkin-types' element={<Navigate to="/plans?tab=encounter-types" replace />} />
              <Route path='/admin/checkin-types/:checkinTypeId' element={<RedirectLegacyCheckinType />} />
            </Route>

            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Router>
      </Provider>
    </AuthProvider>
    </ConfigProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
